import type { CameraZone } from '../types/camera-zones';
import type { CornerRadii, HandlePart, ZoneCorner, ZonePoint, ZoneRect } from '../types/canvas-zones';

/** Both axes of the zone coordinate grid run 0..10000, independent of stream resolution. */
export const ZONE_POINTS = 10_000;

/** Centred quarter-frame box, wound clockwise from the top-left like every zone we write. */
export const DEFAULT_COORDINATES: CameraZone['coordinates'] = [
	ZONE_POINTS * 0.25,
	ZONE_POINTS * 0.25,
	ZONE_POINTS * 0.75,
	ZONE_POINTS * 0.25,
	ZONE_POINTS * 0.75,
	ZONE_POINTS * 0.75,
	ZONE_POINTS * 0.25,
	ZONE_POINTS * 0.75,
];

/** Smallest zone the editor will produce, as a fraction of the frame on each axis. */
export const ZONE_MIN_SIZE = 0.02;

/** How much the handle core swells once it is flat against an edge. */
const CORE_GROWTH = 0.2;

/** Arm thickness and length, as fractions of the handle radius. */
const ARM_THICKNESS = 0.35;
const ARM_REACH = 2.6;

export const clamp = (value: number, min: number, max: number): number => {
	return Math.min(Math.max(value, min), max);
};

/**
 * Read stored coordinates into a rectangle. Point order carries no meaning here:
 * any winding, starting corner or point count resolves to the same bounding
 * rectangle, and values written out of range by another client are clamped.
 *
 * @param coordinates - Flat x, y pairs on the 0..10000 grid.
 * @returns The bounding rectangle in 0..1 frame space.
 */
export const rectFromCoordinates = (coordinates: CameraZone['coordinates']): ZoneRect => {
	const xs: number[] = [];
	const ys: number[] = [];

	for (let i = 0; i + 1 < (coordinates?.length ?? 0); i += 2) {
		xs.push(clamp(coordinates[i] / ZONE_POINTS, 0, 1));
		ys.push(clamp(coordinates[i + 1] / ZONE_POINTS, 0, 1));
	}

	if (xs.length === 0) return { x1: 0, y1: 0, x2: 1, y2: 1 };

	return {
		x1: Math.min(...xs),
		y1: Math.min(...ys),
		x2: Math.max(...xs),
		y2: Math.max(...ys),
	};
};

/**
 * Write a rectangle back as the four corners in clockwise order starting at the
 * top-left, which is the layout every other client expects. Values are clamped
 * to the grid so an out-of-frame rectangle can never reach the device.
 *
 * @param rect - Rectangle in 0..1 frame space.
 * @returns Eight integers on the 0..10000 grid.
 */
export const rectToCoordinates = (rect: ZoneRect): CameraZone['coordinates'] => {
	const toGrid = (value: number) => Math.round(clamp(value, 0, 1) * ZONE_POINTS);

	const left = toGrid(Math.min(rect.x1, rect.x2));
	const top = toGrid(Math.min(rect.y1, rect.y2));
	const right = toGrid(Math.max(rect.x1, rect.x2));
	const bottom = toGrid(Math.max(rect.y1, rect.y2));

	return [left, top, right, top, right, bottom, left, bottom];
};

/** Where the handle for a corner sits. */
export const cornerPoint = (rect: ZoneRect, corner: ZoneCorner): ZonePoint => {
	return corner === 'topLeft' ? { x: rect.x1, y: rect.y1 } : { x: rect.x2, y: rect.y2 };
};

/** The corner a resize pivots around, which is the one without the dragged handle. */
export const oppositeCorner = (rect: ZoneRect, corner: ZoneCorner): ZonePoint => {
	return cornerPoint(rect, corner === 'topLeft' ? 'bottomRight' : 'topLeft');
};

export const isInsideRect = (rect: ZoneRect, x: number, y: number): boolean => {
	return x >= rect.x1 && x <= rect.x2 && y >= rect.y1 && y <= rect.y2;
};

/**
 * Place a dragged corner on one axis. It may cross to the far side of the anchor
 * so the zone flips, but it always stays in the frame and keeps ZONE_MIN_SIZE of
 * separation, falling back to the other side when the anchor sits on an edge.
 *
 * @param desired - Where the pointer wants the corner, in 0..1.
 * @param anchor - The opposite corner on the same axis.
 * @returns A position the zone can actually take.
 */
const resolveDraggedAxis = (desired: number, anchor: number): number => {
	const roomAfter = anchor + ZONE_MIN_SIZE <= 1;
	const roomBefore = anchor - ZONE_MIN_SIZE >= 0;
	const after = desired >= anchor ? roomAfter : !roomBefore;

	return after ? clamp(desired, anchor + ZONE_MIN_SIZE, 1) : clamp(desired, 0, anchor - ZONE_MIN_SIZE);
};

/** Where a dragged corner may actually sit, given the anchor it pivots around. */
export const clampDraggedCorner = (anchor: ZonePoint, x: number, y: number): ZonePoint => {
	return {
		x: resolveDraggedAxis(x, anchor.x),
		y: resolveDraggedAxis(y, anchor.y),
	};
};

/** The rectangle spanned by two opposite corners, whichever way round they are. */
export const rectFromCorners = (a: ZonePoint, b: ZonePoint): ZoneRect => {
	return {
		x1: Math.min(a.x, b.x),
		y1: Math.min(a.y, b.y),
		x2: Math.max(a.x, b.x),
		y2: Math.max(a.y, b.y),
	};
};

/**
 * Translate the rectangle so the grab point follows (x, y). The translation is
 * clamped rather than rejected, so a pointer outside the frame slides the
 * rectangle along the border instead of freezing it.
 *
 * @param rect - Rectangle being moved.
 * @param x - Pointer position in 0..1 frame space.
 * @param y - Pointer position in 0..1 frame space.
 * @param grabOffsetX - Where inside the rectangle the gesture started.
 * @param grabOffsetY - Where inside the rectangle the gesture started.
 * @returns The translated rectangle, same size as the one given.
 */
export const moveRect = (
	rect: ZoneRect,
	x: number,
	y: number,
	grabOffsetX: number,
	grabOffsetY: number,
): ZoneRect => {
	const width = rect.x2 - rect.x1;
	const height = rect.y2 - rect.y1;

	const left = clamp(x - grabOffsetX, 0, 1 - width);
	const top = clamp(y - grabOffsetY, 0, 1 - height);

	return { x1: left, y1: top, x2: left + width, y2: top + height };
};

/** 0 while the handle is clear of both edges on this axis, 1 when flat against one. */
const edgePressure = (position: number, extent: number, radius: number): number => {
	return 1 - clamp(Math.min(position, extent - position) / radius, 0, 1);
};

/**
 * The largest rounded rectangle centred on the corner that fits inside the
 * frame. With room on every side it is exactly a circle of `half` radius; where
 * it lands on a frame corner that corner takes the frame's own radius, so the
 * handle nests into the rounded video instead of looking sliced by it.
 *
 * @param x - Corner position in CSS pixels.
 * @param y - Corner position in CSS pixels.
 * @param half - Half-extent the core would like on every side.
 * @param width - Frame width in CSS pixels.
 * @param height - Frame height in CSS pixels.
 * @param pressure - How far the handle is into an edge, 0 to 1.
 * @param frameRadius - Corner radius the frame itself is clipped to.
 * @returns The core shape.
 */
const seatedCore = (
	x: number,
	y: number,
	half: number,
	width: number,
	height: number,
	pressure: number,
	frameRadius: number,
): HandlePart => {
	const left = Math.min(half, x);
	const right = Math.min(half, width - x);
	const top = Math.min(half, y);
	const bottom = Math.min(half, height - y);

	const partWidth = left + right;
	const partHeight = top + bottom;

	const base = Math.min(partWidth, partHeight) / 2;
	const outer = base + (frameRadius - base) * pressure;

	const radii: CornerRadii = [base, base, base, base];
	if (left < half && top < half) radii[0] = outer;
	if (right < half && top < half) radii[1] = outer;
	if (right < half && bottom < half) radii[2] = outer;
	if (left < half && bottom < half) radii[3] = outer;

	return { x: x - left, y: y - top, width: partWidth, height: partHeight, radii };
};

/**
 * Shapes making up one handle: a core that seats itself into the frame, plus an
 * arm along each edge it has reached. The arms are the signal, saying which
 * sides are pinned without the user having to read the zone outline. Arms hug
 * their edge once pressed but stay centred on the handle until then, and start
 * clear of the frame's corner curve so their cap is never what gets cropped.
 *
 * @param x - Corner position in CSS pixels.
 * @param y - Corner position in CSS pixels.
 * @param radius - Handle radius with room to spare, in CSS pixels.
 * @param width - Frame width in CSS pixels.
 * @param height - Frame height in CSS pixels.
 * @param frameRadius - Corner radius the frame itself is clipped to.
 * @returns The core first, then any arms. Every part is inside the frame.
 */
export const handleParts = (
	x: number,
	y: number,
	radius: number,
	width: number,
	height: number,
	frameRadius: number,
): HandlePart[] => {
	const pressureX = edgePressure(x, width, radius);
	const pressureY = edgePressure(y, height, radius);
	const pressure = Math.max(pressureX, pressureY);
	const half = radius * (1 + CORE_GROWTH * pressure);

	const parts = [seatedCore(x, y, half, width, height, pressure, frameRadius)];

	const thickness = radius * ARM_THICKNESS;
	const cap = thickness / 2;
	const reach = radius * ARM_REACH;

	if (pressureY > 0) {
		const length = reach * pressureY;
		const growsRight = x < width / 2;
		const along = growsRight ? Math.max(x, frameRadius) : Math.min(x, width - frameRadius) - length;

		parts.push({
			x: along,
			y: clamp(y - cap, 0, height - thickness),
			width: length,
			height: thickness,
			radii: [cap, cap, cap, cap],
		});
	}

	if (pressureX > 0) {
		const length = reach * pressureX;
		const growsDown = y < height / 2;
		const along = growsDown ? Math.max(y, frameRadius) : Math.min(y, height - frameRadius) - length;

		parts.push({
			x: clamp(x - cap, 0, width - thickness),
			y: along,
			width: thickness,
			height: length,
			radii: [cap, cap, cap, cap],
		});
	}

	return parts;
};

/** Shrinks a part on every side, for stroking a ring inside its own outline. */
export const insetPart = (part: HandlePart, amount: number): HandlePart => {
	return {
		x: part.x + amount,
		y: part.y + amount,
		width: Math.max(0, part.width - amount * 2),
		height: Math.max(0, part.height - amount * 2),
		radii: part.radii.map((radius) => Math.max(0, radius - amount)) as CornerRadii,
	};
};

/**
 * Find the handle under a pointer. Distances are measured in pixels so the grab
 * area stays circular on a non-square frame, and only the two corners that carry
 * a handle are considered.
 *
 * @param rect - The zone being edited.
 * @param pointerX - Pointer position in CSS pixels.
 * @param pointerY - Pointer position in CSS pixels.
 * @param width - Frame width in CSS pixels.
 * @param height - Frame height in CSS pixels.
 * @param radius - Grab radius in CSS pixels.
 * @returns The nearest corner in reach, or null.
 */
export const hitTestCorner = (
	rect: ZoneRect,
	pointerX: number,
	pointerY: number,
	width: number,
	height: number,
	radius: number,
): ZoneCorner | null => {
	const corners: ZoneCorner[] = ['topLeft', 'bottomRight'];

	let closest: ZoneCorner | null = null;
	let closestDistance = Infinity;

	for (const corner of corners) {
		const center = cornerPoint(rect, corner);
		const distance = Math.hypot(pointerX - center.x * width, pointerY - center.y * height);

		if (distance <= radius && distance < closestDistance) {
			closest = corner;
			closestDistance = distance;
		}
	}

	return closest;
};

export const darkenColor = (color: CameraZone['color'], factor: number = 0.3): CameraZone['color'] => {
	return [Math.round(color[0] * factor), Math.round(color[1] * factor), Math.round(color[2] * factor)];
};
